import type { HistoryItem } from '@/lib/api';
import { entityDisplayName } from '@/lib/history';
import { filterMarkdownTableEmptyColumns } from '@/lib/markdownTableFilter';
import { IressIcon, IressText } from '@iress-oss/ids-components';
import { ChatResponse } from './ChatResponse';
import { CopyButton } from './CopyButton';
import './HistoryDetail.css';

/**
 * Static disclaimer shown on a history entry: the stored response is a
 * point-in-time snapshot and may be stale, so the adviser is told they can
 * re-ask Porter for the latest. Copy per Legal/Compliance (DARE-105).
 */
const HISTORY_STALENESS_DISCLAIMER =
  'Information presented in this response may be outdated. For the latest ' +
  'information, you can ask Porter again.';

interface HistoryDetailProps {
  /** The selected entry, taken directly from the already-fetched list. */
  item: HistoryItem;
  /** ISO date label for the header (e.g. "19 Aug 2026"). */
  dateLabel: string;
  onBack: () => void;
  /** "Search for {client}'s information" — sets context + returns to Chat. */
  onSearchForClient: (item: HistoryItem) => void;
}

/**
 * History entry detail. Rendered entirely from the in-memory list payload —
 * no refetch, no live re-run. Shows the stored query, the stored response
 * (exactly as originally returned, even if now stale), and the entry date.
 */
export function HistoryDetail({
  item,
  dateLabel,
  onBack,
  onSearchForClient,
}: HistoryDetailProps) {
  const name = entityDisplayName(item.entityDetails);

  return (
    <div className="porter-detail">
      <div className="porter-detail__header">
        <button className="porter-detail__back" onClick={onBack} type="button">
          <span className="porter-detail__back-icon">
            <IressIcon name="chevron_left" />
          </span>{' '}
          Back to History
        </button>
        <span className="porter-detail__date">{dateLabel}</span>
      </div>

      <div className="porter-detail__body">
        <div className="porter-chat__user-query">
          <p className="porter-chat__user-query-text">{item.queryText}</p>
        </div>
        <ChatResponse text={item.responseText} />
        {item.status === 'COMPLETE' && (
          <CopyButton text={filterMarkdownTableEmptyColumns(item.responseText)} label="Copy" />
        )}
        <IressText
          element="p"
          color="muted"
          className="porter-detail__disclaimer"
        >
          {HISTORY_STALENESS_DISCLAIMER}
        </IressText>
      </div>

      <div className="porter-detail__footer">
        <button
          className="porter-detail__search"
          onClick={() => onSearchForClient(item)}
          type="button"
        >
          Search for {name}&rsquo;s information
          <span className="porter-detail__search-icon">
            <IressIcon name="chevron_right" />
          </span>
        </button>
      </div>
    </div>
  );
}
